import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatDate, formatNumber, API } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Plus, Trash2, X, Upload, Download, FileSpreadsheet, ChevronDown, ChevronUp, Landmark, ArrowUpRight, ArrowDownLeft, BarChart3, Search, Filter, AlertTriangle, RefreshCw, Wrench, Wand2, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "Belirsiz";
  const [y, mo] = m.split("-");
  const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y}`;
};

const CATEGORIES = [
  "Tedarikçi Ödemesi", "Firma Cari Ödemesi", "Gelen Tahsilat", "Öğrenci Ödemesi",
  "Yakıt", "Araç Bakım/Tamir",
  "Maaş/Personel", "Maaş",
  "Vergi/SGK", "Vergi/Devlet",
  "Kira", "Sigorta", "Ceza",
  "Kredi Ödemesi", "Kredi Kartı", "Hesaplar Arası", "Banka Masrafı",
  "Elektrik", "Doğalgaz", "İnternet/Telefon",
  "Diğer"
];
const CHART_COLORS = ["#0020C2", "#DC2626", "#F59E0B", "#16A34A", "#8B5CF6", "#EC4899", "#0EA5E9", "#84CC16", "#F97316", "#6B7280"];

const empty = () => ({
  date: new Date().toISOString().slice(0,10),
  description: "", counterparty: "", reference: "",
  amount: "", direction: "out", category: "",
  bank_account: "", balance_after: "", notes: "",
});

export default function BankTransactionsPage() {
  const [months, setMonths] = useState([]);
  const [grand, setGrand] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty());
  const [selectedMonth, setSelectedMonth] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [bankAccount, setBankAccount] = useState("");

  // Açıklama / karşı taraf / kategori / yön filtreleri
  const [descFilter, setDescFilter] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [dirFilter, setDirFilter] = useState("all");
  const [bankFilter, setBankFilter] = useState("all");
  const [dupOnly, setDupOnly] = useState(false);

  // Anomaliler modal
  const [showAnom, setShowAnom] = useState(false);
  const [anomData, setAnomData] = useState(null);
  const [anomLoading, setAnomLoading] = useState(false);

  // Yükleme partileri (import batches) modal
  const [showBatches, setShowBatches] = useState(false);
  const [batchData, setBatchData] = useState(null);
  const [batchDeleting, setBatchDeleting] = useState(null);

  // Toplu kategori seçimi
  const [selected, setSelected] = useState(new Set());
  const [bulkCat, setBulkCat] = useState("");

  // Otomatik kategorize modal
  const [showAuto, setShowAuto] = useState(false);
  const [autoData, setAutoData] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoApplying, setAutoApplying] = useState(false);
  const [autoOnlyUncat, setAutoOnlyUncat] = useState(true);

  const openAuto = async () => {
    setShowAuto(true); setAutoLoading(true); setAutoData(null);
    try {
      const { data } = await api.post(`/bank-transactions/auto-categorize-preview?only_uncategorized=${autoOnlyUncat}`);
      setAutoData(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setAutoLoading(false); }
  };

  const applyAuto = async (ids = null) => {
    if (!window.confirm(`${ids ? ids.length : autoData.suggestion_count} kayıt için önerilen kategoriler + fatura no'lar uygulanacak. Devam?`)) return;
    setAutoApplying(true);
    try {
      const { data } = await api.post("/bank-transactions/auto-categorize-apply", ids ? { ids } : {});
      toast.success(`${data.updated} hareket güncellendi`);
      setShowAuto(false); setAutoData(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setAutoApplying(false); }
  };

  const toggleSel = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const selectAllVisible = () => {
    const s = new Set(selected);
    filteredMonths.forEach(g => (g.entries || []).forEach(e => s.add(e.id)));
    setSelected(s);
  };
  const clearSel = () => setSelected(new Set());

  const applyBulkCategory = async () => {
    if (selected.size === 0) { toast.error("Seçili hareket yok"); return; }
    const cat = bulkCat.trim();
    if (!cat) { toast.error("Kategori seçin"); return; }
    if (!window.confirm(`${selected.size} hareket için kategori '${cat}' olarak değiştirilecek. Devam?`)) return;
    try {
      const { data } = await api.post("/bank-transactions/bulk-category", { ids: Array.from(selected), category: cat });
      toast.success(`${data.updated} hareket güncellendi`);
      setSelected(new Set()); setBulkCat("");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openAnomalies = async () => {
    setShowAnom(true); setAnomLoading(true); setAnomData(null);
    try {
      const { data } = await api.get("/bank-transactions/anomalies");
      setAnomData(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setAnomLoading(false); }
  };

  const flipDir = async (id) => {
    try {
      await api.post(`/bank-transactions/${id}/flip-direction`);
      toast.success("Yön düzeltildi");
      await load();
      if (showAnom) await openAnomalies();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/bank-transactions/by-month");
      setMonths(data.months || []);
      setGrand(data.grand || {});
    } catch (e) { toast.error(errMsg(e)); }
  };
  const loadAnalysis = async (month) => {
    try {
      const { data } = await api.get(`/bank-transactions/analysis${month ? `?month=${month}` : ""}`);
      setAnalysis(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); loadAnalysis(""); }, []);
  useEffect(() => { loadAnalysis(selectedMonth); }, [selectedMonth]);

  const openBatches = async () => {
    setShowBatches(true);
    setBatchData(null);
    try {
      const { data } = await api.get("/bank-transactions/import-batches");
      setBatchData(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteBatch = async (b, isLast) => {
    const label = b.source_file || `${formatDate(b.date_min)} – ${formatDate(b.date_max)} dönemi`;
    if (!window.confirm(`${isLast ? "SON YÜKLEME: " : ""}"${label}" partisindeki ${b.count} banka hareketi kalıcı olarak silinecek. Emin misiniz?`)) return;
    setBatchDeleting(b.key);
    try {
      const { data } = await api.delete(`/bank-transactions/import-batches/${encodeURIComponent(b.key)}`);
      toast.success(`${data.deleted} hareket silindi`);
      const { data: fresh } = await api.get("/bank-transactions/import-batches");
      setBatchData(fresh);
      load();
      loadAnalysis(selectedMonth);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBatchDeleting(null); }
  };

  const toggle = (m) => {
    const s = new Set(expanded);
    if (s.has(m)) s.delete(m); else s.add(m);
    setExpanded(s);
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = Math.abs(Number(form.amount) || 0);
    const payload = {
      ...form,
      amount: form.direction === "in" ? amt : -amt,
      balance_after: Number(form.balance_after) || 0,
      month: (form.date || "").slice(0,7),
    };
    try {
      await api.post("/bank-transactions", payload);
      toast.success("Kaydedildi");
      setShowForm(false); setForm(empty()); load(); loadAnalysis(selectedMonth);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm("Banka hareketini silmek istiyor musunuz?")) return;
    try { await api.delete(`/bank-transactions/${r.id}`); toast.success("Silindi"); load(); loadAnalysis(selectedMonth); }
    catch (err) { toast.error(errMsg(err)); }
  };

  // Turkish-safe fold + filter
  const fold = (s) => (s || "").toLocaleLowerCase("tr-TR")
    .replaceAll("ı","i").replaceAll("ş","s").replaceAll("ç","c")
    .replaceAll("ğ","g").replaceAll("ü","u").replaceAll("ö","o");

  // Collect unique banks (bank_account) from all entries
  const bankOptions = (() => {
    const s = new Set();
    (months || []).forEach(g => (g.entries || []).forEach(e => { if (e.bank_account) s.add(e.bank_account); }));
    return Array.from(s).sort();
  })();

  // Collect all categories actually present in data (union with predefined list)
  const categoryOptions = (() => {
    const s = new Set(CATEGORIES);
    (months || []).forEach(g => (g.entries || []).forEach(e => { if (e.category) s.add(e.category); }));
    return Array.from(s).sort();
  })();

  // Detect duplicate entries (same description + amount + date within global set)
  const dupKeys = (() => {
    const counts = {};
    (months || []).forEach(g => (g.entries || []).forEach(e => {
      const k = `${e.date || ""}|${Math.abs(e.amount || 0).toFixed(2)}|${fold(e.description || "")}`;
      counts[k] = (counts[k] || 0) + 1;
    }));
    const set = new Set();
    Object.entries(counts).forEach(([k, c]) => { if (c > 1) set.add(k); });
    return set;
  })();
  const dupKeyOf = (r) => `${r.date || ""}|${Math.abs(r.amount || 0).toFixed(2)}|${fold(r.description || "")}`;
  const totalDuplicates = (() => {
    let n = 0;
    (months || []).forEach(g => (g.entries || []).forEach(e => { if (dupKeys.has(dupKeyOf(e))) n++; }));
    return n;
  })();

  const matchesFilters = (r) => {
    if (dirFilter !== "all" && r.direction !== dirFilter) return false;
    if (catFilter !== "all") {
      if (catFilter === "__uncategorized__") { if (r.category) return false; }
      else if ((r.category || "") !== catFilter) return false;
    }
    if (bankFilter !== "all") {
      if (bankFilter === "__nobank__") { if (r.bank_account) return false; }
      else if ((r.bank_account || "") !== bankFilter) return false;
    }
    if (dupOnly && !dupKeys.has(dupKeyOf(r))) return false;
    const q = fold(descFilter.trim());
    if (!q) return true;
    const hay = fold([r.description, r.counterparty, r.reference, r.bank_account, r.category, r.notes].filter(Boolean).join(" "));
    return hay.includes(q);
  };

  // Recompute per-month aggregates on filtered entries
  const filteredMonths = months.map(g => {
    const entries = (g.entries || []).filter(matchesFilters);
    if (entries.length === 0) return null;
    const total_in = entries.filter(e => e.direction === "in").reduce((a, e) => a + Math.abs(e.amount || 0), 0);
    const total_out = entries.filter(e => e.direction === "out").reduce((a, e) => a + Math.abs(e.amount || 0), 0);
    return { ...g, entries, count: entries.length, total_in, total_out, net: total_in - total_out };
  }).filter(Boolean);

  const updateCategory = async (r, category) => {
    try {
      await api.put(`/bank-transactions/${r.id}`, {
        date: r.date, description: r.description, counterparty: r.counterparty || "",
        reference: r.reference || "", amount: r.amount, direction: r.direction,
        category, bank_account: r.bank_account || "", balance_after: r.balance_after || 0,
        month: r.month || "", notes: r.notes || "",
      });
      load(); loadAnalysis(selectedMonth);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const uploadXlsx = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (bankAccount) fd.append("bank_account", bankAccount);
    try {
      const { data } = await api.post("/bank-transactions/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} banka hareketi yüklendi${data.skipped ? ` (${data.skipped} atlandı)` : ""}`);
      load(); loadAnalysis(selectedMonth);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/bank-transactions/template/xlsx?auth=${token || ""}`, "_blank");
  };

  const dailyChartData = analysis?.daily || [];
  const categoryChartData = (analysis?.by_category || []).filter(c => c.total_out > 0).slice(0, 8);
  const categoryChartDataIn = (analysis?.by_category || []).filter(c => c.total_in > 0).slice(0, 8);
  const topInData = (analysis?.top_counterparties_in || []).filter(c => c.total_in > 0).slice(0, 8);

  const purgeAll = async () => {
    if (!window.confirm("TÜM banka hareketleri kalıcı olarak silinecek. Bu işlem GERİ ALINAMAZ. Devam edilsin mi?")) return;
    if (!window.confirm("Son onay: TÜM banka hareketlerini sil?")) return;
    try {
      const { data } = await api.post("/admin/purge-bank-transactions-all");
      toast.success(`${data.deleted} banka hareketi silindi`);
      load();
      loadAnalysis(selectedMonth);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageHeader
        title="Banka Hareketleri"
        subtitle="Aylık banka ekstrelerini yükleyin. Nereye ne kadar ödeme gitmiş görün ve kategorize edin."
        testid="bank-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <input placeholder="Banka/Hesap (opsiyonel)" value={bankAccount} onChange={e => setBankAccount(e.target.value)} className="px-3 py-2.5 text-xs border border-[#E5E5E5] w-40" />
            <button data-testid="bank-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Download className="w-3.5 h-3.5" /> Boş Excel
            </button>
            <label className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5] cursor-pointer">
              <FileSpreadsheet className="w-3.5 h-3.5" /> <span data-testid="bank-xlsx-upload">Ekstre Yükle</span>
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadXlsx(f); e.target.value = ""; }} />
            </label>
            <button data-testid="bank-batches-btn" onClick={openBatches} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <History className="w-3.5 h-3.5" /> Yüklemeler
            </button>
            <button data-testid="bank-auto-cat-btn" onClick={openAuto} className="flex items-center gap-2 bg-[#7C3AED] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#6D28D9]">
              <Wand2 className="w-4 h-4" strokeWidth={2.2} /> Otomatik Kategorize
            </button>
            <button data-testid="bank-anomalies-btn" onClick={openAnomalies} className="flex items-center gap-2 bg-[#F59E0B] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#D97706]">
              <Wrench className="w-4 h-4" strokeWidth={2.2} /> Kontrol Et
            </button>
            <button data-testid="bank-add-btn" onClick={() => { setForm(empty()); setShowForm(true); }} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2]">
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Manuel Ekle
            </button>
            <button data-testid="bank-purge-all-btn" onClick={purgeAll} className="flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#B91C1C]">
              <Trash2 className="w-4 h-4" strokeWidth={2.2} /> Tümünü Sil
            </button>
          </div>
        }
      />

      {/* Grand totals — filtreye göre dinamik */}
      {(() => {
        const fGrand = filteredMonths.reduce((acc, g) => ({
          total_in: acc.total_in + (g.total_in || 0),
          total_out: acc.total_out + (g.total_out || 0),
          count: acc.count + (g.count || 0),
        }), { total_in: 0, total_out: 0, count: 0 });
        fGrand.net = fGrand.total_in - fGrand.total_out;
        const isFiltered = (descFilter || catFilter !== "all" || dirFilter !== "all" || bankFilter !== "all" || dupOnly);
        return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Gelen {isFiltered && <span className="text-[#0020C2]">(filtreli)</span>}</div>
          <div className="num font-bold text-xl mt-1 text-[#16A34A]">{formatTL(fGrand.total_in)}</div>
          {isFiltered && <div className="text-[10px] text-neutral-400 num">Toplam: {formatTL(grand.total_in || 0)}</div>}
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Giden {isFiltered && <span className="text-[#0020C2]">(filtreli)</span>}</div>
          <div className="num font-bold text-xl mt-1 text-[#DC2626]">{formatTL(fGrand.total_out)}</div>
          {isFiltered && <div className="text-[10px] text-neutral-400 num">Toplam: {formatTL(grand.total_out || 0)}</div>}
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Net Akış {isFiltered && <span className="text-[#0020C2]">(filtreli)</span>}</div>
          <div className={`num font-bold text-xl mt-1 ${fGrand.net >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(fGrand.net)}</div>
          {isFiltered && <div className="text-[10px] text-neutral-400 num">Toplam: {formatTL(grand.net || 0)}</div>}
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Hareket Sayısı {isFiltered && <span className="text-[#0020C2]">(filtreli)</span>}</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(fGrand.count)}</div>
          {isFiltered && <div className="text-[10px] text-neutral-400 num">Toplam: {formatNumber(grand.count || 0)}</div>}
        </div>
      </div>
        );
      })()}

      {/* Analysis panel */}
      <div className="bg-white border border-[#E5E5E5] p-5 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#0020C2]" />
            <div className="font-heading font-bold text-base">Ödeme Analizi</div>
          </div>
          <select data-testid="analysis-month-select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="px-3 py-2 text-xs border border-[#E5E5E5]">
            <option value="">Tüm Aylar</option>
            {months.map(m => <option key={m.month} value={m.month}>{monthLabel(m.month)}</option>)}
          </select>
        </div>

        {analysis && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top counterparties out */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">En Çok Ödeme Yapılan (Giden)</div>
              {analysis.top_counterparties_out.length === 0 ? (
                <div className="text-xs text-neutral-400 py-8 text-center">Veri yok</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={analysis.top_counterparties_out.slice(0, 8)} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} style={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="counterparty" width={110} style={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => formatTL(v)} />
                    <Bar dataKey="total_out" fill="#DC2626" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Category pie */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Kategori Bazlı Giden</div>
              {categoryChartData.length === 0 ? (
                <div className="text-xs text-neutral-400 py-8 text-center">Kategori atanmış veri yok — aşağıdan atayın.</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={categoryChartData} dataKey="total_out" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={(e) => e.category}>
                      {categoryChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatTL(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Top counterparties IN */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#16A34A] mb-2 font-semibold">En Çok Ödeme Alınan (Gelen)</div>
              {topInData.length === 0 ? (
                <div className="text-xs text-neutral-400 py-8 text-center">Gelen veri yok</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={topInData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} style={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="counterparty" width={110} style={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => formatTL(v)} />
                    <Bar dataKey="total_in" fill="#16A34A" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Category pie INCOMING (donut) */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#16A34A] mb-2 font-semibold">Kategori Bazlı Gelen</div>
              {categoryChartDataIn.length === 0 ? (
                <div className="text-xs text-neutral-400 py-8 text-center">Gelen kategori atanmış veri yok</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={categoryChartDataIn} dataKey="total_in" nameKey="category" cx="50%" cy="50%" innerRadius={55} outerRadius={95} label={(e) => e.category}>
                      {categoryChartDataIn.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatTL(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Daily flow */}
            {dailyChartData.length > 1 && (
              <div className="lg:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Günlük Nakit Akışı</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={dailyChartData}>
                    <XAxis dataKey="date" style={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} style={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => formatTL(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="total_in" fill="#16A34A" name="Gelen" />
                    <Bar dataKey="total_out" fill="#DC2626" name="Giden" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filtre paneli */}
      <div className="bg-white border border-[#E5E5E5] rounded-md p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px] relative">
          <Search className="w-3.5 h-3.5 text-neutral-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input data-testid="bank-desc-filter" value={descFilter} onChange={e => setDescFilter(e.target.value)}
            placeholder="Açıklama, karşı taraf, referans, banka veya kategoride ara..."
            className="w-full pl-8 pr-3 py-2 text-sm border border-[#E5E5E5] focus:border-[#0020C2] outline-none" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-neutral-500" />
          <select data-testid="bank-bank-filter" value={bankFilter} onChange={e => setBankFilter(e.target.value)}
            className="px-2 py-1.5 text-xs border border-[#E5E5E5] bg-white max-w-[220px]">
            <option value="all">Tüm bankalar ({bankOptions.length})</option>
            <option value="__nobank__">(Banka atanmamış)</option>
            {bankOptions.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="bank-cat-filter" value={catFilter} onChange={e => setCatFilter(e.target.value)}
            className="px-2 py-1.5 text-xs border border-[#E5E5E5] bg-white max-w-[220px]">
            <option value="all">Tüm kategoriler ({categoryOptions.length})</option>
            <option value="__uncategorized__">(Kategorisiz)</option>
            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="bank-dir-filter" value={dirFilter} onChange={e => setDirFilter(e.target.value)}
            className="px-2 py-1.5 text-xs border border-[#E5E5E5] bg-white">
            <option value="all">Gelen + Giden</option>
            <option value="in">Sadece Gelen</option>
            <option value="out">Sadece Giden</option>
          </select>
          <button data-testid="bank-dup-toggle" onClick={() => setDupOnly(!dupOnly)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-semibold border transition-colors ${
              dupOnly ? "bg-[#F59E0B] text-white border-[#F59E0B]" : "border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-white"
            }`}
            title="Aynı tarih + tutar + açıklamalı hareketleri göster">
            <AlertTriangle className="w-3 h-3" /> Mükerrer ({totalDuplicates})
          </button>
          {(descFilter || catFilter !== "all" || dirFilter !== "all" || bankFilter !== "all" || dupOnly) && (
            <button data-testid="bank-filter-clear" onClick={() => { setDescFilter(""); setCatFilter("all"); setDirFilter("all"); setBankFilter("all"); setDupOnly(false); }}
              className="px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white">
              Filtreleri Temizle
            </button>
          )}
        </div>
      </div>

      {/* Toplu İşlem Çubuğu */}
      {selected.size > 0 && (
        <div className="bg-[#DBEAFE] border border-[#0020C2] rounded-md p-3 mb-4 flex items-center gap-3 flex-wrap sticky top-0 z-20 shadow-sm">
          <span className="text-xs font-semibold text-[#0020C2]"><span className="num">{selected.size}</span> hareket seçildi</span>
          <select data-testid="bulk-cat-select" value={bulkCat} onChange={e => setBulkCat(e.target.value)} className="px-2 py-1.5 text-xs border border-[#E5E5E5] bg-white">
            <option value="">— Kategori seçin —</option>
            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button data-testid="bulk-cat-apply" onClick={applyBulkCategory} disabled={!bulkCat}
            className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0018A0] disabled:opacity-40">
            Kategoriyi Uygula
          </button>
          <button onClick={selectAllVisible} className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white">
            Görünenleri Seç
          </button>
          <button onClick={clearSel} className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-neutral-300 hover:bg-neutral-100">
            Seçimi Temizle
          </button>
        </div>
      )}

      {/* Duplicate warning banner */}
      {totalDuplicates > 0 && !dupOnly && (
        <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-md p-3 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
            <span><strong className="num">{totalDuplicates}</strong> hareket <strong>aynı tarih + tutar + açıklamayla mükerrer</strong> — yanlışlıkla iki kez yüklenmiş olabilir.</span>
          </div>
          <button onClick={() => setDupOnly(true)} className="px-3 py-1 text-[10px] uppercase tracking-widest font-semibold bg-[#F59E0B] text-white hover:bg-[#D97706]">
            Mükerrerleri Göster
          </button>
        </div>
      )}

      {/* Monthly groups */}
      <div className="space-y-3">
        {filteredMonths.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] p-16 text-center text-sm text-neutral-500">
            <Landmark className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
            {months.length === 0 ? "Henüz banka hareketi yok. Excel ile ekstre yükleyerek başlayın." : "Filtrenizle eşleşen hareket bulunamadı."}
          </div>
        ) : filteredMonths.map(g => {
          const isOpen = expanded.has(g.month);
          return (
            <div key={g.month} className="bg-white border border-[#E5E5E5] rounded-md" data-testid={`bank-month-${g.month}`}>
              <button onClick={() => toggle(g.month)} className="w-full px-5 py-4 flex items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                  <div>
                    <div className="font-heading font-bold text-lg">{monthLabel(g.month)}</div>
                    <div className="text-xs text-neutral-500 mt-0.5"><span className="num font-semibold">{g.count}</span> hareket</div>
                  </div>
                </div>
                <div className="flex items-center gap-5 flex-wrap">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Gelen</div>
                    <div className="num font-bold text-sm text-[#16A34A]">{formatTL(g.total_in)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Giden</div>
                    <div className="num font-bold text-sm text-[#DC2626]">{formatTL(g.total_out)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Net</div>
                    <div className={`num font-bold text-sm ${g.net >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(g.net)}</div>
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-[#F5F5F5] p-4 space-y-4">
                  {/* Counterparty breakdown */}
                  {g.by_counterparty.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Karşı Taraf Kırılımı (En Çok Ödeme)</div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {g.by_counterparty.slice(0, 9).map(cp => (
                          <div key={cp.counterparty} className="border border-[#F5F5F5] p-2 text-xs bg-[#FAFAFA]">
                            <div className="font-semibold truncate">{cp.counterparty}</div>
                            <div className="flex justify-between mt-1 text-neutral-600">
                              <span className="text-[#16A34A]">↓ {formatTL(cp.total_in)}</span>
                              <span className="text-[#DC2626]">↑ {formatTL(cp.total_out)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Entries */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA]">
                        <tr>
                          <th className="px-3 py-2 w-8 text-left">
                            <input type="checkbox" data-testid={`bank-group-check-${g.month}`}
                              checked={g.entries.length > 0 && g.entries.every(e => selected.has(e.id))}
                              onChange={e => {
                                const s = new Set(selected);
                                if (e.target.checked) g.entries.forEach(x => s.add(x.id));
                                else g.entries.forEach(x => s.delete(x.id));
                                setSelected(s);
                              }} />
                          </th>
                          {["Tarih","Açıklama","Karşı Taraf","Kategori","Yön","Tutar",""].map((h, i) => (
                            <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 5 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {g.entries.map(r => {
                          const isDup = dupKeys.has(dupKeyOf(r));
                          const isSel = selected.has(r.id);
                          return (
                          <tr key={r.id} className={`border-t border-[#F5F5F5] ${isSel ? "bg-[#DBEAFE]" : isDup ? "bg-[#FEF3C7]" : ""}`} title={isDup ? "Aynı tarih + tutar + açıklamayla başka hareket(ler) var — mükerrer olabilir" : undefined}>
                            <td className="px-3 py-2">
                              <input type="checkbox" data-testid={`bank-row-check-${r.id}`} checked={isSel} onChange={() => toggleSel(r.id)} />
                            </td>
                            <td className="px-3 py-2 num whitespace-nowrap">
                              {isDup && <AlertTriangle className="w-3 h-3 inline text-[#F59E0B] mr-1" />}
                              {formatDate(r.date)}
                            </td>
                            <td className="px-3 py-2 max-w-[280px] truncate" title={r.description}>{r.description}</td>
                            <td className="px-3 py-2 text-neutral-600 max-w-[180px] truncate">{r.counterparty || "-"}</td>
                            <td className="px-3 py-2">
                              <input list={`cat-list-${r.id}`} defaultValue={r.category || ""} onBlur={e => { if (e.target.value !== (r.category || "")) updateCategory(r, e.target.value); }} className="w-32 px-2 py-1 bg-white border border-[#E5E5E5] text-xs" />
                              <datalist id={`cat-list-${r.id}`}>{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
                            </td>
                            <td className="px-3 py-2">
                              {r.direction === "in" ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#16A34A]"><ArrowDownLeft className="w-3 h-3" /> GELEN</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#DC2626]"><ArrowUpRight className="w-3 h-3" /> GİDEN</span>
                              )}
                              <button data-testid={`bank-flip-${r.id}`} onClick={() => flipDir(r.id)}
                                title={`Yönü ${r.direction === "in" ? "GİDEN'e" : "GELEN'e"} çevir`}
                                className="ml-1 p-0.5 hover:bg-[#F5F5F5] rounded text-neutral-500 hover:text-[#F59E0B]">
                                <RefreshCw className="w-3 h-3" />
                              </button>
                            </td>
                            <td className={`px-3 py-2 text-right num font-semibold ${r.direction === "in" ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(Math.abs(r.amount))}</td>
                            <td className="px-3 py-2 text-right">
                              <button onClick={() => remove(r)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Manual form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Manuel</div>
                <div className="font-heading font-bold text-lg">Banka Hareketi</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                <input type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yön *</label>
                <select value={form.direction} onChange={e => setForm({...form, direction: e.target.value})} className="input mt-1.5">
                  <option value="out">Giden (Ödeme)</option>
                  <option value="in">Gelen (Tahsilat)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Açıklama *</label>
                <input required value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Karşı Taraf</label>
                <input value={form.counterparty} onChange={e => setForm({...form, counterparty: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Kategori</label>
                <input list="cat-list-form" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input mt-1.5" />
                <datalist id="cat-list-form">{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                <input type="number" step="any" required value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Referans / Dekont</label>
                <input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Banka Hesabı</label>
                <input value={form.bank_account} onChange={e => setForm({...form, bank_account: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Bakiye (opsiyonel)</label>
                <input type="number" step="any" value={form.balance_after} onChange={e => setForm({...form, balance_after: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Anomaliler Modal */}
      {showAnom && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAnom(false)}>
          <div className="bg-white w-full max-w-6xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Otomatik Denetim</div>
                <div className="font-heading font-bold text-lg">Banka Hareketleri Kontrolü</div>
              </div>
              <button onClick={() => setShowAnom(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              {anomLoading && <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[#0020C2] mx-auto mb-2" /><div className="text-xs text-neutral-500">Denetleniyor...</div></div>}

              {anomData && !anomLoading && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Hareket</div>
                      <div className="num font-bold text-lg">{anomData.total_transactions}</div>
                    </div>
                    <div className="border border-[#F59E0B] p-3 bg-[#FEF3C7]">
                      <div className="text-[10px] uppercase tracking-widest text-[#B45309] font-semibold">Şüpheli Yön/Eksik Bilgi</div>
                      <div className="num font-bold text-lg text-[#B45309]">{anomData.anomaly_count}</div>
                    </div>
                    <div className="border border-[#DC2626] p-3 bg-[#FEE2E2]">
                      <div className="text-[10px] uppercase tracking-widest text-[#DC2626] font-semibold">Mükerrer (Aynı Trh+Trt+Açk)</div>
                      <div className="num font-bold text-lg text-[#DC2626]">{anomData.duplicate_count}</div>
                    </div>
                  </div>

                  {/* Anomalies */}
                  <div className="border border-[#E5E5E5]">
                    <div className="px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B]" />
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Şüpheli Yön / Eksik Bilgi ({anomData.anomalies.length})</span>
                    </div>
                    <div className="max-h-[280px] overflow-y-auto">
                      {anomData.anomalies.length === 0 ? (
                        <div className="p-6 text-center text-xs text-neutral-500">Şüpheli hareket yok.</div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="bg-white border-b border-[#F5F5F5] sticky top-0">
                            <tr>
                              {["Tarih","Açıklama","Yön","Tutar","Uyarılar","İşlem"].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {anomData.anomalies.map(a => (
                              <tr key={a.id} className="border-t border-[#F5F5F5] hover:bg-[#FEF3C7]">
                                <td className="px-3 py-1.5 num whitespace-nowrap">{a.date}</td>
                                <td className="px-3 py-1.5 max-w-[280px] truncate" title={a.description}>{a.description}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`text-[10px] font-bold uppercase ${a.direction === "in" ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{a.direction === "in" ? "GELEN" : "GİDEN"}</span>
                                </td>
                                <td className={`px-3 py-1.5 num text-right font-semibold ${a.direction === "in" ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(Math.abs(a.amount || 0))}</td>
                                <td className="px-3 py-1.5 text-[#B45309] max-w-[300px]">
                                  {(a.reasons || []).map((r, i) => <div key={i} className="text-[10px]">• {r}</div>)}
                                </td>
                                <td className="px-3 py-1.5">
                                  <button onClick={() => flipDir(a.id)} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold bg-[#F59E0B] text-white hover:bg-[#D97706]" title="Yönü ters çevir (in↔out)">
                                    <RefreshCw className="w-3 h-3" /> Yönü Düzelt
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  {/* Duplicates */}
                  <div className="border border-[#E5E5E5]">
                    <div className="px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-[#DC2626]" />
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Mükerrer Hareketler ({anomData.duplicates.length})</span>
                    </div>
                    <div className="max-h-[280px] overflow-y-auto">
                      {anomData.duplicates.length === 0 ? (
                        <div className="p-6 text-center text-xs text-neutral-500">Mükerrer hareket yok.</div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="bg-white border-b border-[#F5F5F5] sticky top-0">
                            <tr>
                              {["Tarih","Açıklama","Yön","Tutar","Banka","İşlem"].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {anomData.duplicates.map(d => (
                              <tr key={d.id} className="border-t border-[#F5F5F5] hover:bg-[#FEE2E2]">
                                <td className="px-3 py-1.5 num whitespace-nowrap">{d.date}</td>
                                <td className="px-3 py-1.5 max-w-[280px] truncate" title={d.description}>{d.description}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`text-[10px] font-bold uppercase ${d.direction === "in" ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{d.direction === "in" ? "GELEN" : "GİDEN"}</span>
                                </td>
                                <td className={`px-3 py-1.5 num text-right font-semibold ${d.direction === "in" ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(Math.abs(d.amount || 0))}</td>
                                <td className="px-3 py-1.5 text-neutral-500 text-[10px]">{d.bank_account || "-"}</td>
                                <td className="px-3 py-1.5">
                                  <button onClick={async () => { if (window.confirm("Bu mükerrer hareketi sil?")) { try { await api.delete(`/bank-transactions/${d.id}`); toast.success("Silindi"); await openAnomalies(); await load(); } catch (e) { toast.error(errMsg(e)); } } }}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold bg-[#DC2626] text-white hover:bg-[#B91C1C]">
                                    <Trash2 className="w-3 h-3" /> Sil
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Otomatik Kategorize Modal */}
      {showAuto && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAuto(false)}>
          <div className="bg-white w-full max-w-6xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kural Tabanlı Öneri</div>
                <div className="font-heading font-bold text-lg">Otomatik Kategorize + Fatura No Çıkarma</div>
              </div>
              <button onClick={() => setShowAuto(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-3 text-xs text-neutral-700 flex items-center justify-between gap-3 flex-wrap">
                <span>Açıklamayı analiz eder: "TEDARİKÇİ FATURA BEDELİ", "MAAŞ", "OPET", "SGK", "KİRA" gibi anahtar kelimelerle kategori belirler ve fatura numarasını çıkarır.</span>
                <label className="flex items-center gap-2 text-[11px]">
                  <input type="checkbox" checked={autoOnlyUncat} onChange={e => setAutoOnlyUncat(e.target.checked)} />
                  <span>Sadece kategorisiz olanlar</span>
                </label>
                <button onClick={openAuto} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold bg-[#7C3AED] text-white hover:bg-[#6D28D9]">
                  <RefreshCw className="w-3 h-3 inline mr-1" /> Tara
                </button>
              </div>

              {autoLoading && <div className="p-12 text-center"><RefreshCw className="w-6 h-6 mx-auto animate-spin text-[#7C3AED] mb-2" /><div className="text-xs text-neutral-500">Analiz ediliyor...</div></div>}

              {autoData && !autoLoading && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Taranan Hareket</div>
                      <div className="num font-bold text-lg">{autoData.total_scanned}</div>
                    </div>
                    <div className="border border-[#7C3AED] p-3 bg-[#EDE9FE]">
                      <div className="text-[10px] uppercase tracking-widest text-[#7C3AED] font-semibold">Bulunan Öneri</div>
                      <div className="num font-bold text-lg text-[#7C3AED]">{autoData.suggestion_count}</div>
                    </div>
                  </div>

                  {autoData.category_breakdown && autoData.category_breakdown.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {autoData.category_breakdown.map(c => (
                        <span key={c.category} className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 bg-[#F5F5F5] border border-[#E5E5E5]">
                          {c.category}: <span className="num text-[#7C3AED]">{c.count}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {autoData.suggestion_count > 0 && (
                    <div className="border border-[#E5E5E5]">
                      <div className="px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Öneriler ({autoData.suggestions.length})</span>
                        <button data-testid="auto-apply-all" onClick={() => applyAuto(null)} disabled={autoApplying}
                          className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-40">
                          {autoApplying ? "Uygulanıyor..." : `Tümünü Uygula (${autoData.suggestion_count})`}
                        </button>
                      </div>
                      <div className="max-h-[420px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-white border-b border-[#F5F5F5] sticky top-0">
                            <tr>
                              {["Tarih","Açıklama","Tutar","Şu Anki Kategori","Önerilen Kategori","Fatura No"].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {autoData.suggestions.map(s => (
                              <tr key={s.id} className="border-t border-[#F5F5F5] hover:bg-[#EDE9FE]">
                                <td className="px-3 py-1.5 num whitespace-nowrap">{s.date}</td>
                                <td className="px-3 py-1.5 max-w-[280px] truncate" title={s.description}>{s.description}</td>
                                <td className={`px-3 py-1.5 num text-right font-semibold ${s.direction === "in" ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(Math.abs(s.amount || 0))}</td>
                                <td className="px-3 py-1.5 text-neutral-500">{s.current_category || "-"}</td>
                                <td className="px-3 py-1.5">
                                  {s.suggested_category && <span className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-[#7C3AED] text-white">{s.suggested_category}</span>}
                                </td>
                                <td className="px-3 py-1.5 num text-[#0020C2] font-semibold">{s.suggested_reference || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {autoData.suggestion_count === 0 && (
                    <div className="p-8 text-center text-xs text-neutral-500">Öneri bulunamadı. Açıklama içinde tanınan anahtar kelime yok veya tüm hareketler zaten kategorize.</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Yükleme Partileri Modal */}
      {showBatches && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBatches(false)}>
          <div className="bg-white w-full max-w-3xl rounded-md shadow-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="batches-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ekstre Yükleme Geçmişi</div>
                <div className="font-heading font-bold text-lg">Yüklemeler {batchData && <span className="text-xs font-normal text-neutral-500">({batchData.batches.length} parti · {formatNumber(batchData.total_transactions)} hareket)</span>}</div>
              </div>
              <button onClick={() => setShowBatches(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6">
              {!batchData ? (
                <div className="p-8 text-center text-sm text-neutral-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</div>
              ) : batchData.batches.length === 0 ? (
                <div className="p-8 text-center text-sm text-neutral-500">Hiç yükleme bulunamadı.</div>
              ) : (
                <div className="space-y-2">
                  {batchData.batches.map((b, idx) => (
                    <div key={b.key} className={`flex items-center justify-between gap-3 border p-3.5 ${idx === 0 ? "border-[#0020C2] bg-[#F5F8FF]" : "border-[#E5E5E5]"}`} data-testid={`batch-row-${idx}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {idx === 0 && <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold bg-[#0020C2] text-white">SON YÜKLEME</span>}
                          <span className="text-sm font-semibold truncate">{b.source_file || "Ekstre yüklemesi"}</span>
                          {b.bank_account && <span className="text-[10px] px-1.5 py-0.5 bg-[#F5F5F5] uppercase tracking-wider">{b.bank_account}</span>}
                          {b.kind === "legacy" && <span className="text-[9px] uppercase tracking-widest text-neutral-400">eski kayıt</span>}
                        </div>
                        <div className="text-xs text-neutral-500 mt-1 flex items-center gap-3 flex-wrap">
                          <span>Yükleme: <span className="num">{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString("tr-TR") : "-"}</span></span>
                          <span>Dönem: <span className="num">{formatDate(b.date_min)} – {formatDate(b.date_max)}</span></span>
                          <span className="num font-semibold">{formatNumber(b.count)} hareket</span>
                          <span className="text-[#16A34A] num">+{formatTL(b.total_in)}</span>
                          <span className="text-[#DC2626] num">-{formatTL(b.total_out)}</span>
                        </div>
                      </div>
                      <button
                        data-testid={`batch-delete-${idx}`}
                        disabled={batchDeleting === b.key}
                        onClick={() => deleteBatch(b, idx === 0)}
                        className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white disabled:opacity-50 transition-colors"
                      >
                        {batchDeleting === b.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Sil
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 text-[11px] text-neutral-500 bg-[#FAFAFA] border border-[#F5F5F5] p-3">
                Eski kayıtlar (yükleme kimliği olmayanlar) yükleme zamanına göre otomatik gruplanır. Yeni yüklemeler dosya adıyla birlikte tam olarak takip edilir.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
